import React, { useEffect, useState, useRef } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { PageTitle } from '../components/layout/PageTitle';
import { Loader2, Camera, Upload, Image as ImageIcon, CheckCircle, XCircle, Search, Library } from 'lucide-react';
import { Button, Upload as AntUpload, message, Card, Empty, Spin, Tooltip, Select, Tag, Modal } from 'antd';
import { ImageSimilarityService } from '../services/CardScanner/ImageHashingService';
import { CardHashStorageService } from '../services/CardScanner/CardHashStorageService';
import { PokemonCardDetectorService, type DetectionResult, type BoundingBox } from '../services/CardScanner/PokemonCardDetectionService';
import { useQuery } from '../hooks/useQuery';
import { Set } from '../types/mtg';
import { PokemonCard } from '../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../lib/constants';
import { AdSense } from '../components/ads/AdSense';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { SEO } from '../components/seo/SEO';
import { useCachedQuery } from '../hooks/useCachedQuery';

interface ExtractedCard {
  imageUrl: string;
  confidence: number;
  hash?: string;
  matchedCard?: {
    id: string;
    distance: number;
    card?: PokemonCard;
  };
  topMatches?: Array<{
    id: string;
    distance: number;
    card: PokemonCard;
  }>;
  selected?: boolean;
  points?: Array<[number, number]>;
  quantity?: number;
  currentQuantity?: number;
  showMatches?: boolean;
}

// Fonction utilitaire pour obtenir le bon chemin d'image
const getImageUrl = (imagePath: string | null | undefined, lang: 'fr' | 'en') => {
  if (!imagePath) return '';
  return `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${imagePath}`;
}

// Fonction utilitaire pour obtenir le nom de la carte en fonction de la langue
const getCardName = (card: PokemonCard | null | undefined, lang: 'fr' | 'en') => {
  if (!card) return '';
  return lang === 'en' ? (card.card_en || card.card_fr || '') : (card.card_fr || card.card_en || '');
}

// Composant pour afficher l'image du set ou son nom
const SetImage = ({ setName }: { setName?: string | null }) => {
  const { lang } = useTranslation();
  const { data: setInfo, loading } = useCachedQuery<{ img: string | null, display_name: string }>(
    'sets',
    { name: setName },
    { single: true, cacheDisabled: !setName }
  );

  if (!setName) {
    return <p className="text-xs text-gray-500">?</p>;
  }

  if (loading) {
    return <p className="text-xs text-gray-500">{setName}</p>;
  }

  if (setInfo?.img) {
    return (
      <div className="flex justify-center items-center h-6">
        <img 
          src={getImageUrl(setInfo.img, lang)}
          alt={setName} 
          className="h-5 w-auto object-contain"
        />
      </div>
    );
  }

  return <p className="text-xs text-gray-500">{setName}</p>;
};

export function CardScanner() {
  const { t, lang } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [extractedCards, setExtractedCards] = useState<ExtractedCard[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [allCards, setAllCards] = useState<PokemonCard[]>([]);
  const [uniqueCards, setUniqueCards] = useState<PokemonCard[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bulkQuantity, setBulkQuantity] = useState<number>(1);
  const [isUpdatingCollection, setIsUpdatingCollection] = useState(false);
  const [userCards, setUserCards] = useState<Record<number, number>>({});
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const cardsCache = useRef<Map<string, PokemonCard[]>>(new Map());
  
  const { data: sets } = useCachedQuery<Set[]>('sets', {
    orderBy: 'id'
  }, { single: false });

  const detectorService = PokemonCardDetectorService.getInstance();
  const hashingService = ImageSimilarityService.getInstance();
  const hashStorageService = CardHashStorageService.getInstance();
  const navigate = useNavigate();

  // Chargement du modèle et des cartes une seule fois
  useEffect(() => {
    const loadModelAndCards = async () => {
      try {
        setIsLoading(true);
        // Charger le modèle
        await detectorService.loadModel(`${window.location.origin}/model/model.json`);
        setIsModelLoaded(true);
        
        // Vérifier si les cartes sont déjà en cache
        const cacheKey = 'all_cards';
        const cachedCards = localStorage.getItem(cacheKey);
        
        if (cachedCards) {
          try {
            const { data, timestamp } = JSON.parse(cachedCards);
            // Cache valide pendant 1 heure
            if (Date.now() - timestamp < 60 * 60 * 1000) {
              setAllCards(data);
              setUniqueCards(data);
              console.log('Cartes chargées depuis le cache');
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error('Erreur lors de la lecture du cache des cartes:', error);
          }
        }
        
        // Si pas de cache ou cache expiré, charger depuis Supabase avec pagination
        const pageSize = 1000;
        let allLoadedCards: any[] = [];
        let page = 0;
        
        while (true) {
          const { data, error } = await supabase
            .from('cards')
            .select('*')
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (error) {
            throw error;
          }

          if (!data || data.length === 0) break;

          allLoadedCards = [...allLoadedCards, ...data];

          if (data.length < pageSize) break;
          page++;
        }
        
        // Mettre en cache
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ data: allLoadedCards, timestamp: Date.now() })
          );
        } catch (cacheError) {
          console.error('Erreur lors de la mise en cache des cartes:', cacheError);
        }
        
        setAllCards(allLoadedCards);
        setUniqueCards(allLoadedCards);
        
        console.log('Modèle et cartes chargés avec succès');
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        message.error(t('card_scanner.loading_error'));
      } finally {
        setIsLoading(false);
      }
    };

    loadModelAndCards();
  }, []);

  // Charger les cartes de l'utilisateur
  useEffect(() => {
    const loadUserCards = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('user_cards')
          .select('card_id, quantity')
          .eq('user_id', user.id);

        if (error) throw error;

        // Créer un objet avec les quantités actuelles
        const quantities = data.reduce((acc, curr) => {
          acc[curr.card_id] = curr.quantity;
          return acc;
        }, {} as Record<number, number>);

        setUserCards(quantities);
      } catch (error) {
        console.error('Erreur lors du chargement des cartes de l\'utilisateur:', error);
      }
    };

    loadUserCards();
  }, []);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const file = imageFiles[0];
    setUploadedImage(file);
    setPreviewImage(URL.createObjectURL(file));

    try {
      setIsProcessing(true);
      message.info(t('card_scanner.analysis_in_progress'));

      // Détecter les cartes dans l'image
      const results = await detectorService.detectImages([file]);
      
      if (results.length === 0 || results[0].detections.length === 0) {
        message.warning(t('card_scanner.no_cards_detected'));
        setIsProcessing(false);
        return;
      }

      // Extraire les cartes détectées
      const extracted = await extractCardImages(file, results[0]);
      
      if (extracted.length === 0) {
        message.warning(t('card_scanner.extraction_error'));
        setIsProcessing(false);
        return;
      }

      setExtractedCards(extracted);
      const validCardsCount = extracted.filter(card => {
        if (!card.topMatches || card.topMatches.length === 0) return false;
        const initialMatch = card.topMatches[0];
        const initialSimilarity = calculateSimilarity(initialMatch.distance, card.hash?.length || 1);
        return initialSimilarity >= 80;
      }).length;
      message.success(t('card_scanner.cards_detected_success', { count: validCardsCount }));
    } catch (error) {
      console.error('Erreur lors de l\'analyse de l\'image:', error);
      message.error(t('card_scanner.extraction_error'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Extraire les images des cartes
  const extractCardImages = async (file: File, detections: DetectionResult) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    return new Promise<ExtractedCard[]>((resolve) => {
      image.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Impossible d\'obtenir le contexte 2D pour le canvas');
          resolve([]);
          return;
        }

        try {
          // Créer une clé de cache pour cette session d'analyse
          const sessionCacheKey = `card_hashes_${Date.now()}`;
          let storedHashes: any[] = [];
          
          // Vérifier si les hashes stockés sont déjà en cache (localStorage)
          const cachedHashes = localStorage.getItem('stored_card_hashes');
          if (cachedHashes) {
            try {
              const { data, timestamp } = JSON.parse(cachedHashes);
              // Cache valide pendant 1 heure
              if (Date.now() - timestamp < 60 * 60 * 1000) {
                storedHashes = data;
                console.log('Hashes chargés depuis le cache local');
              }
            } catch (error) {
              console.error('Erreur lors de la lecture du cache des hashes:', error);
            }
          }
          
          // Si le cache est vide, récupérer les hashes depuis le service
          if (storedHashes.length === 0) {
            storedHashes = await hashStorageService.getAllHashes();
            // Mettre en cache
            try {
              localStorage.setItem(
                'stored_card_hashes',
                JSON.stringify({ data: storedHashes, timestamp: Date.now() })
              );
            } catch (cacheError) {
              console.error('Erreur lors de la mise en cache des hashes:', cacheError);
            }
          }

          // Cache pour les calculs de hash (en mémoire pour cette session)
          const hashCache = new Map<string, string>();
          // Cache pour les distances de Hamming calculées
          const distanceCache = new Map<string, number>();

          const extractedCardsPromises = detections.detections
            .filter((detection: BoundingBox) => detection.confidence >= 10) // Seuil de confiance plus bas
            .map(async (detection: BoundingBox) => {
              try {
                const points = detection.points as [number, number][];
                const [x1, y1] = points[0];
                const [x2, y2] = points[2];
                const width = x2 - x1;
                const height = y2 - y1;

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(image, x1, y1, width, height, 0, 0, width, height);

                const cardImageUrl = canvas.toDataURL('image/jpeg', 0.8);
                
                // Calculer le hash ou utiliser le cache
                let hash = '';
                const hashCacheKey = `${width}_${height}_${x1}_${y1}_${x2}_${y2}`;
                
                if (hashCache.has(hashCacheKey)) {
                  hash = hashCache.get(hashCacheKey) || '';
                } else {
                  try {
                    hash = await hashingService.calculatePerceptualHash(cardImageUrl);
                    hashCache.set(hashCacheKey, hash);
                  } catch (error) {
                    console.error('Erreur lors du calcul du hash pour l\'image extraite:', error);
                    hash = '';
                  }
                }

                // Calculer les distances pour toutes les cartes et les trier avec cache
                const matches = storedHashes
                  .filter((storedHash: any) => storedHash.hash) // Vérifier que le hash existe
                  .map((storedHash: any) => {
                    const distanceCacheKey = `${hash}_${storedHash.hash}`;
                    let distance;
                    
                    if (distanceCache.has(distanceCacheKey)) {
                      distance = distanceCache.get(distanceCacheKey) || 0;
                    } else {
                      distance = hashingService.calculateHammingDistance(hash, storedHash.hash);
                      distanceCache.set(distanceCacheKey, distance);
                    }
                    
                    const matchedCard = uniqueCards.find((card) => String(card.id) === storedHash.id);
                    if (!matchedCard) {
                      return null;
                    }
                    return {
                      id: storedHash.id,
                      distance,
                      card: matchedCard,
                    };
                  })
                  .filter((match: any): match is { id: string; distance: number; card: PokemonCard } => match !== null)
                  .sort((a: any, b: any) => a.distance - b.distance);

                // Obtenir les 5 meilleures correspondances
                const topMatches = matches.slice(0, 5);

                // La meilleure correspondance est la première
                const bestMatch = topMatches.length > 0 ? topMatches[0] : undefined;

                const extractedCard: ExtractedCard = {
                  imageUrl: cardImageUrl,
                  confidence: detection.confidence,
                  hash,
                  matchedCard: bestMatch
                    ? {
                        id: bestMatch.id,
                        distance: bestMatch.distance,
                        card: bestMatch.card,
                      }
                    : undefined,
                  topMatches,
                  selected: true,
                  points: points,
                  quantity: 1,
                  currentQuantity: bestMatch?.card?.id ? (userCards[Number(bestMatch.card.id)] || 0) : 0,
                  showMatches: false,
                };
                return extractedCard;
              } catch (error) {
                console.error('Erreur lors du traitement de la carte détectée:', error);
                return null;
              }
            });

          const extractedCardsWithNulls = await Promise.all(extractedCardsPromises);
          
          // Filtrer les valeurs nulles
          const validExtractedCards = extractedCardsWithNulls.filter((card): card is ExtractedCard => card !== null);
          
          // Regrouper les cartes identiques (même ID de carte) et ajuster les quantités
          const uniqueCardMap = new Map<string, ExtractedCard>();
          
          validExtractedCards.forEach(card => {
            if (card.matchedCard?.card?.id) {
              const cardId = String(card.matchedCard.card.id);
              
              if (uniqueCardMap.has(cardId)) {
                // Si la carte existe déjà, augmenter sa quantité
                const existingCard = uniqueCardMap.get(cardId)!;
                existingCard.quantity = (existingCard.quantity || 1) + 1;
              } else {
                // Sinon, ajouter la nouvelle carte à la map
                uniqueCardMap.set(cardId, card);
              }
            } else {
              // Si la carte n'a pas d'ID (carte non reconnue), la traiter comme unique
              // Utiliser un identifiant temporaire basé sur l'index
              uniqueCardMap.set(`unknown-${uniqueCardMap.size}`, card);
            }
          });
          
          // Convertir la Map en tableau pour l'affichage
          const dedupedExtractedCards = Array.from(uniqueCardMap.values());
          
          URL.revokeObjectURL(imageUrl);
          resolve(dedupedExtractedCards);
        } catch (error) {
          console.error('Erreur dans extractCardImages:', error);
          URL.revokeObjectURL(imageUrl);
          resolve([]);
        }
      };
      
      image.onerror = (error) => {
        console.error('Erreur lors du chargement de l\'image pour extraction:', error);
        URL.revokeObjectURL(imageUrl);
        resolve([]);
      };
      
      image.src = imageUrl;
    });
  };

  const handleSelectChange = (cardIndex: number, matchId: string) => {
    setExtractedCards(prevCards => {
      const newCards = [...prevCards];
      const card = newCards[cardIndex];
      
      if (card) {
        const newMatch = card.topMatches?.find(match => match.id === matchId);
        
        if (newMatch) {
          card.matchedCard = {
            id: newMatch.id,
            distance: newMatch.distance,
            card: newMatch.card
          };
          if (newMatch.card.id) {
            card.currentQuantity = userCards[Number(newMatch.card.id)] || 0;
          }
        }
      }
      
      return newCards;
    });
  };

  const getConfidenceColor = (similarity: number) => {
    if (similarity >= 90) return 'success';
    if (similarity >= 70) return 'warning';
    return 'error';
  };

  const getConfidenceText = (similarity: number) => {
    if (similarity >= 90) return 'Excellent';
    if (similarity >= 70) return 'Bon';
    return 'Faible';
  };

  const getConfidenceDisplay = (similarity: number) => {
    const confidenceText = getConfidenceText(similarity);
    const percentage = similarity.toFixed(1);
    return `${confidenceText} (${percentage}% de confiance)`;
  };

  const getValidSelectedCards = (cards: ExtractedCard[]) => {
    return cards.filter(card => 
      card.selected && 
      card.matchedCard && 
      card.matchedCard.distance <= 20 && // 80% de similarité = distance <= 20
      card.matchedCard.card?.id // Vérifier que l'ID existe
    );
  };

  const getUpdateSummary = () => {
    const selectedCards = getValidSelectedCards(extractedCards);

    return selectedCards.map(card => {
      const cardId = card.matchedCard?.card?.id;
      if (!cardId) return null;
      const existingQuantity = userCards[cardId] || 0;
      const newQuantity = existingQuantity + (card.quantity || 1);
      
      return {
        name: getCardName(card.matchedCard?.card, lang),
        currentQuantity: existingQuantity,
        quantityToAdd: card.quantity || 1,
        newTotal: newQuantity
      };
    }).filter(Boolean);
  };

  const handleUpdateCollection = async () => {
    try {
      setIsUpdatingCollection(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        message.error(t('card_scanner.user_not_logged_in'));
        return;
      }

      const selectedCards = getValidSelectedCards(extractedCards);

      if (selectedCards.length === 0) {
        message.warning(t('card_scanner.no_valid_card'));
        return;
      }

      const updates = selectedCards.map(card => {
        if (!card.matchedCard?.card?.id) return null;
        const cardId = parseInt(String(card.matchedCard.card.id));
        const existingQuantity = userCards[cardId] || 0;
        const newQuantity = existingQuantity + (card.quantity || 1);
        
        return {
          user_id: user.id,
          card_id: cardId,
          quantity: newQuantity
        };
      }).filter((update): update is NonNullable<typeof update> => update !== null);

      // Mettre à jour la base de données
      const { error } = await supabase
        .from('user_cards')
        .upsert(updates, { onConflict: 'user_id,card_id' });

      if (error) throw error;

      // Mettre à jour la collection locale
      const newUserCards = { ...userCards };
      updates.forEach(update => {
        newUserCards[update.card_id] = update.quantity;
      });
      setUserCards(newUserCards);

      message.success(t('card_scanner.cards_added_success', { count: selectedCards.length }));
      setIsConfirmModalVisible(false);
      
      // Utiliser la langue actuelle pour la redirection
      const setsPath = lang === 'en' ? '/en/sets' : '/sets';
      navigate(setsPath);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la collection:', error);
      message.error(t('card_scanner.collection_update_error'));
    } finally {
      setIsUpdatingCollection(false);
    }
  };

  // Ajouter une fonction utilitaire pour calculer la similarité
  const calculateSimilarity = (distance: number, hashLength: number) => {
    return 100 - (distance / hashLength) * 100;
  };

  const renderCardMatches = (card: ExtractedCard, index: number) => {
    if (!card.matchedCard) return null;
    
    const similarity = 100 - (card.matchedCard.distance / (card.hash?.length || 1)) * 100;
    const confidenceColor = getConfidenceColor(similarity);
    const confidenceText = getConfidenceText(similarity);
    
    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t('card_scanner.results_title')}:</span>
            <Tag color={confidenceColor}>{getConfidenceDisplay(similarity)}</Tag>
          </div>
          <Button 
            type="text" 
            size="small" 
            onClick={() => {
              setExtractedCards(prevCards => 
                prevCards.map((c, i) => 
                  i === index ? { ...c, showMatches: !c.showMatches } : c
                )
              );
            }}
            icon={<Search className="w-4 h-4" />}
          >
            {card.showMatches ? t('card_scanner.hide_alternatives') : t('card_scanner.see_alternatives')}
          </Button>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="w-24 h-32 flex-shrink-0 relative rounded overflow-hidden border border-gray-200">
            {card.matchedCard.card && (
              <img 
                src={getImageUrl(card.matchedCard.card.image, lang)}
                alt={getCardName(card.matchedCard.card, lang)} 
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div className="flex-grow">
            {card.matchedCard.card && (
              <>
                <h4 className="font-medium text-gray-900">{getCardName(card.matchedCard.card, lang)}</h4>
                <p className="text-sm text-gray-500 text-center"><SetImage setName={card.matchedCard?.card?.set} /></p>
                <p className="text-sm text-gray-500 text-center">{card.matchedCard.card.rarity || '?'}</p>
              </>
            )}
          </div>
        </div>
        
        {card.topMatches && card.topMatches.length > 1 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{t('card_scanner.see_alternatives')}:</span>
              <Button 
                type="text" 
                size="small" 
                onClick={() => {
                  setExtractedCards(prevCards => 
                    prevCards.map((c, i) => 
                      i === index ? { ...c, showMatches: !c.showMatches } : c
                    )
                  );
                }}
                icon={<Search className="w-4 h-4" />}
                className="!text-indigo-600 hover:!text-indigo-700"
              >
                {card.showMatches ? t('card_scanner.hide_alternatives') : t('card_scanner.see_alternatives')}
              </Button>
            </div>
            
            {card.showMatches && (
              <Select
                className="w-full"
                value={card.matchedCard?.id}
                onChange={(value) => handleSelectChange(index, value)}
                options={card.topMatches?.map(match => ({
                  value: match.id,
                  label: (
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-12 flex-shrink-0">
                        <img 
                          src={getImageUrl(match.card.image, lang)}
                          alt={getCardName(match.card, lang)}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="text-sm font-medium truncate">
                          {getCardName(match.card, lang)}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {(100 - (match.distance / (card.hash?.length || 1)) * 100).toFixed(1)}% de correspondance
                        </div>
                      </div>
                    </div>
                  )
                }))}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <SEO 
        title={t('card_scanner.seo.title')}
        description={t('card_scanner.seo.description')}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 md:pb-0">
        {/* En-tête amélioré */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 px-4 py-5 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-shrink-0 bg-gradient-to-br from-indigo-500 to-purple-500 p-3 rounded-xl shadow-lg shadow-indigo-500/20">
                  <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {t('card_scanner.title')}
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">
                    {t('card_scanner.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Annonce AdSense en haut */}
        <div className="mb-6 bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex justify-center items-center">
          <AdSense adSlot="1424794127" />
        </div>
        
        {isLoading ? (
          <div className="bg-white p-8 sm:p-12 rounded-xl shadow-lg border border-gray-100">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-4 border-indigo-500/20 animate-ping"></div>
                  <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-600 animate-spin" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                  <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
              </div>
              <h3 className="mt-6 text-lg sm:text-xl font-medium text-gray-900">{t('card_scanner.loading_model')}</h3>
              <p className="mt-2 text-sm text-gray-500 text-center max-w-md">
                {t('card_scanner.loading_description')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Section d'upload d'image améliorée */}
            {!uploadedImage && (
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
                <h2 className="text-lg font-medium text-gray-900 mb-4">{t('card_scanner.upload_title')}</h2>
                
                <div className="space-y-4">
                  {/* Guide d'utilisation amélioré */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-indigo-100">
                    <h3 className="text-base font-medium text-indigo-900 mb-3">{t('card_scanner.how_to_use')}</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                          1
                        </div>
                        <div>
                          <p className="text-sm text-indigo-900 font-medium">{t('card_scanner.step1_title')}</p>
                          <p className="text-sm text-indigo-700 mt-1">
                            {t('card_scanner.step1_description')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                          2
                        </div>
                        <div>
                          <p className="text-sm text-indigo-900 font-medium">{t('card_scanner.step2_title')}</p>
                          <p className="text-sm text-indigo-700 mt-1">
                            {t('card_scanner.step2_description')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                          3
                        </div>
                        <div>
                          <p className="text-sm text-indigo-900 font-medium">{t('card_scanner.step3_title')}</p>
                          <p className="text-sm text-indigo-700 mt-1">
                            {t('card_scanner.step3_description')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                          4
                        </div>
                        <div>
                          <p className="text-sm text-indigo-900 font-medium">{t('card_scanner.step4_title')}</p>
                          <p className="text-sm text-indigo-700 mt-1">
                            {t('card_scanner.step4_description')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-indigo-100">
                      <p className="text-sm text-indigo-700">
                        <span className="font-medium">{t('card_scanner.tip')}</span> {t('card_scanner.tip_content')}
                      </p>
                    </div>
                  </div>

                  {/* Zone d'upload améliorée */}
                  <div
                    className="file-input-container flex flex-col items-center justify-center p-6 sm:p-8 border-2 border-dashed border-indigo-200 rounded-xl cursor-pointer hover:bg-indigo-50/50 transition-all duration-200"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <p className="flex justify-center mb-3">
                      <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-600" />
                    </p>
                    <p className="text-sm sm:text-base text-gray-600 text-center">
                      {t('card_scanner.upload_prompt')}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-400 mt-2">
                      {t('card_scanner.supported_formats')}
                    </p>
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleImageUpload} 
                      accept="image/*" 
                      className="w-full hidden" 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section de résumé après l'upload améliorée */}
            {uploadedImage && !isProcessing && extractedCards.length === 0 && (
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  {/* Prévisualisation de l'image */}
                  <div className="w-full sm:w-1/2">
                    <div className="relative aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      <img 
                        src={previewImage || ''} 
                        alt="Image téléchargée" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Informations de l'analyse */}
                  <div className="w-full sm:w-1/2">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Résumé de l'analyse</h2>
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center gap-2 text-gray-600">
                          <ImageIcon className="w-5 h-5" />
                          <span className="font-medium">{t('card_scanner.uploaded_image')}:</span>
                          <span>{uploadedImage.name}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          {(uploadedImage.size / 1024).toFixed(2)} KB
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                        <h3 className="text-sm font-medium text-indigo-900 mb-2">{t('card_scanner.next_step')}</h3>
                        <p className="text-sm text-indigo-700">
                          {t('card_scanner.analysis_message')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Section des résultats améliorée */}
            {isProcessing ? (
              <div className="bg-white p-8 sm:p-12 rounded-xl shadow-lg border border-gray-100">
                <div className="flex flex-col items-center justify-center">
                  <div className="relative">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-full border-4 border-indigo-500/20 animate-ping"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-indigo-500/20 animate-pulse"></div>
                      <Search className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-600 animate-spin" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                      <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                  </div>
                  <h3 className="mt-6 text-lg sm:text-xl font-medium text-gray-900">{t('card_scanner.analysis_in_progress')}</h3>
                  <p className="mt-2 text-sm text-gray-500 text-center max-w-md">
                    {t('card_scanner.processing_message')}
                  </p>
                  {previewImage && (
                    <div className="mt-8 w-full max-w-md">
                      <div className="relative aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
                        <img 
                          src={previewImage} 
                          alt={t('card_scanner.image_in_analysis')} 
                          className="w-full h-full object-contain opacity-75"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-white/50 to-transparent"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : extractedCards.length > 0 ? (
              <div className="space-y-6">
                {/* Résumé des cartes détectées amélioré */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    {/* Prévisualisation de l'image */}
                    <div className="w-full sm:w-1/4">
                      <div className="relative aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
                        <img 
                          src={previewImage || ''} 
                          alt="Image téléchargée" 
                          className="w-full h-full object-contain opacity-75"
                        />
                      </div>
                    </div>
                    
                    {/* Statistiques de détection améliorées */}
                    <div className="w-full sm:w-3/4">
                      <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Search className="w-5 h-5 text-indigo-600" />
                        {t('card_scanner.results_title')}
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg text-white">
                              <Search className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-indigo-600">
                                {extractedCards.filter(card => {
                                  if (!card.topMatches || card.topMatches.length === 0) return false;
                                  const initialMatch = card.topMatches[0];
                                  const initialSimilarity = calculateSimilarity(initialMatch.distance, card.hash?.length || 1);
                                  return initialSimilarity >= 80;
                                }).length}
                              </div>
                              <div className="text-sm text-indigo-900 font-medium">
                                {t('card_scanner.cards_detected')}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg text-white">
                              <CheckCircle className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-green-600">
                                {extractedCards.filter(card => {
                                  if (!card.topMatches || card.topMatches.length === 0) return false;
                                  const initialMatch = card.topMatches[0];
                                  const initialSimilarity = calculateSimilarity(initialMatch.distance, card.hash?.length || 1);
                                  return card.selected && initialSimilarity >= 80;
                                }).length}
                              </div>
                              <div className="text-sm text-green-900 font-medium">
                                {t('card_scanner.cards_selected')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Liste des cartes détectées améliorée */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
                  <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Library className="w-5 h-5 text-indigo-600" />
                    {t('card_scanner.detected_cards')}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {extractedCards.map((card, index) => {
                      const initialMatch = card.topMatches?.[0];
                      const similarity = initialMatch 
                        ? calculateSimilarity(initialMatch.distance, card.hash?.length || 1)
                        : 0;
                      
                      if (!card.topMatches || card.topMatches.length === 0 || similarity < 80) {
                        return null;
                      }

                      return (
                        <div 
                          key={index} 
                          className={`bg-white rounded-xl overflow-hidden transition-all ${
                            card.selected 
                              ? "ring-2 ring-indigo-600 shadow-lg border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50" 
                              : "border border-gray-200 hover:border-indigo-200 hover:shadow-md"
                          }`}
                        >
                          {/* En-tête de la carte avec le score de confiance */}
                          <div className="px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                            <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2">
                              <Tag color={similarity >= 90 ? "success" : similarity >= 80 ? "processing" : "error"} 
                                   className="flex items-center gap-1 !text-xs sm:!text-sm overflow-hidden">
                                {similarity >= 90 && <CheckCircle className="w-3 h-3 flex-shrink-0" />}
                                <span className="truncate">{getConfidenceDisplay(similarity)}</span>
                              </Tag>
                              <Button
                                type={card.selected ? "primary" : "default"}
                                size="small"
                                onClick={() => {
                                  setExtractedCards(prevCards => 
                                    prevCards.map((c, i) => 
                                      i === index ? { ...c, selected: !c.selected } : c
                                    )
                                  );
                                }}
                                className={`flex-shrink-0 !min-w-0 !px-2 sm:!px-3 ${
                                  card.selected 
                                    ? "!bg-gradient-to-r from-indigo-600 to-purple-600 hover:!from-indigo-700 hover:!to-purple-700 !border-transparent" 
                                    : ""
                                }`}
                                icon={card.selected ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                              >
                                <span className="whitespace-nowrap">{card.selected ? t('card_scanner.selected') : t('card_scanner.select')}</span>
                              </Button>
                            </div>
                          </div>

                          <div className="p-4">
                            {/* Modification de la structure pour éviter l'étirement */}
                            <div className="flex flex-col gap-4">
                              {/* Image extraite et informations de base */}
                              <div className="flex gap-4">
                                {/* Image de la carte détectée (réduite) */}
                                <div className="w-20 h-28 flex-shrink-0">
                                  <div className="relative w-full h-full rounded-lg overflow-hidden bg-gray-50 border border-gray-200 shadow-sm">
                                    <img 
                                      src={card.imageUrl} 
                                      alt={`Carte détectée ${index + 1}`} 
                                      className="w-full h-full object-contain opacity-90"
                                    />
                                  </div>
                                </div>

                                {/* Informations de la carte */}
                                {card.matchedCard && card.matchedCard.card && (
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-gray-50 to-white border border-gray-200 shadow-sm">
                                      <div className="w-16 h-24 flex-shrink-0 bg-white rounded-lg p-1 border border-gray-100 shadow-sm">
                                        <img 
                                          src={getImageUrl(card.matchedCard.card.image, lang)}
                                          alt={getCardName(card.matchedCard.card, lang)}
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-gray-900 text-sm">
                                          {getCardName(card.matchedCard.card, lang)}
                                        </h4>
                                        <div className="mt-2 space-y-1">
                                          <div className="flex items-center justify-center text-xs text-gray-600">
                                            <SetImage setName={card.matchedCard?.card?.set} />
                                          </div>
                                          <div className="flex items-center justify-center text-xs text-gray-600">
                                            {card.matchedCard.card.rarity}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Contrôles de quantité améliorés */}
                              <div className="mt-4 space-y-3">
                                {/* Quantité actuelle */}
                                <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 shadow-sm">
                                  <div className="px-3 sm:px-4 py-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                                        <span className="text-xs sm:text-sm text-gray-600 truncate">{t('card_scanner.current_collection')}</span>
                                      </div>
                                      <span className="text-base sm:text-lg font-semibold text-gray-900">{card.currentQuantity || 0}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Quantité à ajouter */}
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 shadow-sm">
                                  <div className="px-3 sm:px-4 py-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                        <span className="text-xs sm:text-sm text-indigo-700 truncate">{t('card_scanner.to_add')}</span>
                                      </div>
                                      <div className="flex items-center gap-2 sm:gap-3">
                                        <Tooltip title={t('card_scanner.decrease_quantity')}>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              setExtractedCards(prevCards =>
                                                prevCards.map((c, i) =>
                                                  i === index ? { ...c, quantity: Math.max(1, (c.quantity || 1) - 1) } : c
                                                )
                                              );
                                            }}
                                            className="!min-w-[28px] sm:!min-w-[32px] !w-7 sm:!w-8 !h-7 sm:!h-8 !p-0 !border-indigo-200 hover:!border-indigo-300 !text-indigo-600"
                                          >
                                            -
                                          </Button>
                                        </Tooltip>
                                        <span className="text-base sm:text-lg font-semibold text-indigo-700 min-w-[20px] sm:min-w-[24px] text-center">
                                          {card.quantity || 1}
                                        </span>
                                        <Tooltip title={t('card_scanner.increase_quantity')}>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              setExtractedCards(prevCards =>
                                                prevCards.map((c, i) =>
                                                  i === index ? { ...c, quantity: (c.quantity || 1) + 1 } : c
                                                )
                                              );
                                            }}
                                            className="!min-w-[28px] sm:!min-w-[32px] !w-7 sm:!w-8 !h-7 sm:!h-8 !p-0 !border-indigo-200 hover:!border-indigo-300 !text-indigo-600"
                                          >
                                            +
                                          </Button>
                                        </Tooltip>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Total après ajout */}
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100 shadow-sm">
                                  <div className="px-3 sm:px-4 py-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                        <span className="text-xs sm:text-sm text-green-700 truncate">{t('card_scanner.total_after')}</span>
                                      </div>
                                      <span className="text-base sm:text-lg font-semibold text-green-700">
                                        {(card.currentQuantity || 0) + (card.quantity || 1)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Alternatives améliorées */}
                              <div className="mt-3">
                                <Button
                                  type="text"
                                  size="small"
                                  block
                                  onClick={() => {
                                    setExtractedCards(prevCards => 
                                      prevCards.map((c, i) => 
                                        i === index ? { ...c, showMatches: !c.showMatches } : c
                                      )
                                    );
                                  }}
                                  className="!text-indigo-600 hover:!text-indigo-700"
                                >
                                  {card.showMatches ? t('card_scanner.hide_alternatives') : t('card_scanner.see_alternatives')}
                                </Button>

                                {card.showMatches && card.topMatches && (
                                  <Select
                                    className="w-full mt-2"
                                    value={card.matchedCard?.id}
                                    onChange={(value) => handleSelectChange(index, value)}
                                    options={card.topMatches?.map(match => ({
                                      value: match.id,
                                      label: (
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-12 flex-shrink-0">
                                            <img 
                                              src={getImageUrl(match.card.image, lang)}
                                              alt={getCardName(match.card, lang)}
                                              className="w-full h-full object-contain"
                                            />
                                          </div>
                                          <div className="flex-grow min-w-0">
                                            <div className="text-sm font-medium truncate">
                                              {getCardName(match.card, lang)}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">
                                              {(100 - (match.distance / (card.hash?.length || 1)) * 100).toFixed(1)}% de correspondance
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    }))}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Barre d'actions améliorée */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center justify-center sm:justify-start">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse"></div>
                          <span className="text-sm font-medium text-gray-700">
                            {getValidSelectedCards(extractedCards).length}  {t('card_scanner.cards_selected')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <Button
                          onClick={() => {
                            setExtractedCards([]);
                            setUploadedImage(null);
                            setPreviewImage(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          disabled={extractedCards.length === 0}
                          className="!border-gray-300 hover:!border-gray-400"
                        >
                          {t('card_scanner.cancel')}
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => setIsConfirmModalVisible(true)}
                          disabled={extractedCards.filter(card => card.selected).length === 0}
                          className="!bg-gradient-to-r from-indigo-600 to-purple-600 hover:!from-indigo-700 hover:!to-purple-700 !border-transparent"
                        >
                          {t('card_scanner.update_collection')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal de confirmation amélioré */}
                <Modal
                  title={t('card_scanner.confirm_update_title')}
                  open={isConfirmModalVisible}
                  onCancel={() => setIsConfirmModalVisible(false)}
                  width="95%"
                  style={{ maxWidth: "600px" }}
                  className="!max-h-[90vh]"
                  bodyStyle={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}
                  footer={[
                    <Button key="cancel" onClick={() => setIsConfirmModalVisible(false)}>
                      {t('card_scanner.cancel')}
                    </Button>,
                    <Button
                      key="submit"
                      type="primary"
                      loading={isUpdatingCollection}
                      onClick={handleUpdateCollection}
                      className="!bg-gradient-to-r from-indigo-600 to-purple-600 hover:!from-indigo-700 hover:!to-purple-700"
                    >
                      {t('card_scanner.confirm')}
                    </Button>
                  ]}
                >
                  <div className="space-y-3">
                    <p className="text-xs sm:text-sm text-gray-600">
                      {t('card_scanner.confirm_update_message')}
                    </p>
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl divide-y divide-gray-200">
                      {getUpdateSummary().map((summary, index) => {
                        if (!summary) return null;
                        const matchedCard = extractedCards.find(c => c.matchedCard?.card?.card_fr === summary.name || c.matchedCard?.card?.card_en === summary.name)?.matchedCard?.card;
                        return (
                          <div key={index} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3">
                            <div className="w-12 sm:w-16 h-18 sm:h-24 flex-shrink-0">
                              <img 
                                src={getImageUrl(matchedCard?.image, lang)} 
                                alt={getCardName(matchedCard, lang)}
                                className="w-full h-full object-contain rounded-lg"
                              />
                            </div>
                            <div className="flex-grow min-w-0">
                              <p className="font-medium text-gray-900 truncate text-xs sm:text-sm">{getCardName(matchedCard, lang)}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="text-xs text-gray-500">
                                  {summary.currentQuantity} → {summary.newTotal}
                                </div>
                                <div className="text-xs font-medium text-indigo-600">
                                  +{summary.quantityToAdd}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Modal>
              </div>
            ) : uploadedImage ? (
              <div className="bg-white p-8 text-center rounded-xl shadow-lg border border-gray-100">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="text-gray-500">
                      Aucune carte détectée. Téléchargez une image pour commencer.
                    </span>
                  }
                />
              </div>
            ) : null}
          </div>
        )}
        
        {/* Annonce AdSense en bas */}
        <div className="mt-6 bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex justify-center items-center">
          <AdSense adSlot="1424794127" />
        </div>
      </div>
    </PageLayout>
  );
} 